import { useEffect, useRef, useState } from "react";

import { ApiError, api } from "../../lib/api.js";

/**
 * What the wall runs on: a key, a poll, and icons that can carry the key.
 *
 * A paired screen is a browser with no session. It was opened once with the
 * key in the URL's fragment — the one part of a URL a server never sees and a
 * log never records — and keeps it in its own storage from then on. Everything
 * the board asks for goes through here with that key, or with the cookie when
 * there is none, which is how a signed-in laptop previews it.
 */

const KEY_STORAGE = "ops_screen_key";
export const POLL_MS = 3000;

/** The key this screen holds, taking one from `#key=…` first and cleaning the URL. */
export function useScreenKey() {
    const [key] = useState(() => {
        try {
            const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ""));
            const offered = fragment.get("key");
            if (offered) {
                localStorage.setItem(KEY_STORAGE, offered);
                window.history.replaceState(null, "", window.location.pathname + window.location.search);
                return offered;
            }
            return localStorage.getItem(KEY_STORAGE);
        } catch {
            return null;
        }
    });
    return key;
}

/**
 * The board, refreshed every few seconds.
 *
 * The last good answer is kept through a failed poll — a screen that blanks
 * every time the network hiccups is a screen that cries wolf. A 401 is
 * different: the key was revoked or there never was one, and the screen says
 * so rather than showing yesterday's board for ever.
 */
export function useBoard(key) {
    const [board, setBoard] = useState(null);
    const [problem, setProblem] = useState(null);
    const [stale, setStale] = useState(false);

    useEffect(() => {
        let alive = true;
        let timer = null;

        const ask = async () => {
            try {
                const next = await api.live.get(key);
                if (!alive) return;
                setBoard(next);
                setProblem(null);
                setStale(false);
            } catch (error) {
                if (!alive) return;
                if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
                    setProblem("unpaired");
                } else {
                    setStale(true);
                    setProblem((current) => current ?? "unreachable");
                }
            } finally {
                if (alive) timer = setTimeout(ask, POLL_MS);
            }
        };

        ask();
        return () => {
            alive = false;
            clearTimeout(timer);
        };
    }, [key]);

    return { board, problem: problem === "unreachable" && board ? null : problem, stale };
}

/*
 * Icons, with the key.
 *
 * `ProjectIcon` draws an <img>, and an <img> sends cookies but never a header.
 * With a key the bytes are fetched here and handed over as an object URL, once
 * per icon: the URL carries the content hash, so a hash already seen is an
 * icon already held — the same reasoning the browser cache applies to the
 * <img> path.
 */
const icons = new Map();

export function useIconUrl(slug, hash, key) {
    const plain = slug && hash ? `/api/projects/${slug}/favicon?v=${hash}` : null;
    const [url, setUrl] = useState(() => (key && plain ? icons.get(plain) ?? null : plain));

    useEffect(() => {
        if (!key || !plain) {
            setUrl(plain);
            return undefined;
        }
        const held = icons.get(plain);
        if (held) {
            setUrl(held);
            return undefined;
        }
        let alive = true;
        fetch(plain, { headers: { authorization: `Bearer ${key}` }, credentials: "omit" })
            .then((response) => (response.ok ? response.blob() : null))
            .then((blob) => {
                if (!blob) return;
                const object = URL.createObjectURL(blob);
                icons.set(plain, object);
                if (alive) setUrl(object);
            })
            .catch(() => {});
        return () => {
            alive = false;
        };
    }, [plain, key]);

    return url;
}

// --- time, as a wall shows it -----------------------------------------------------------

/** "3m", "2h", "5d": read from across a room, the word "ago" is implied. A date still to come reads "in 12d". */
export function since(value, now = Date.now()) {
    if (!value) return "—";
    const delta = Math.round((now - new Date(value).getTime()) / 1000);
    const seconds = Math.abs(delta);
    const word = seconds < 60 ? `${seconds}s` : seconds < 3600 ? `${Math.floor(seconds / 60)}m` : seconds < 86400 ? `${Math.floor(seconds / 3600)}h` : `${Math.floor(seconds / 86400)}d`;
    return delta < -30 ? `in ${word}` : word;
}

/** "02:14" for something happening, hours only once they are needed. */
export function elapsed(from, now = Date.now()) {
    if (!from) return "00:00";
    const total = Math.max(0, Math.floor((now - new Date(from).getTime()) / 1000));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    const mm = String(m).padStart(2, "0");
    const ss = String(s).padStart(2, "0");
    return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

/** "28s", "1m 12s", "1h 03m" for something that is over. */
export function took(ms) {
    if (!ms && ms !== 0) return "—";
    const seconds = Math.round(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${String(seconds % 60).padStart(2, "0")}s`;
    return `${Math.floor(seconds / 3600)}h ${String(Math.floor((seconds % 3600) / 60)).padStart(2, "0")}m`;
}

export function bytes(value) {
    if (value === null || value === undefined) return "—";
    const gb = value / 1024 ** 3;
    return gb >= 100 ? `${Math.round(gb)} GB` : `${gb.toFixed(1)} GB`;
}

/** A value that eases to its next value, so a figure that changes is seen to change. */
export function useTween(value, ms = 700) {
    const [shown, setShown] = useState(value ?? 0);
    const from = useRef(value ?? 0);
    useEffect(() => {
        if (value === null || value === undefined) return undefined;
        const start = performance.now();
        const begin = from.current;
        let frame;
        const step = (t) => {
            const p = Math.min(1, (t - start) / ms);
            const eased = 1 - (1 - p) ** 3;
            const next = begin + (value - begin) * eased;
            // Remembered every frame, so a value that changes mid-tween eases on
            // from where it is rather than jumping back to where it started.
            from.current = next;
            setShown(next);
            if (p < 1) frame = requestAnimationFrame(step);
        };
        frame = requestAnimationFrame(step);
        return () => cancelAnimationFrame(frame);
    }, [value, ms]);
    return shown;
}

// --- what happened, in one list ------------------------------------------------------------

const DEPLOY_WORD = {
    success: "deployed",
    failed: "failed",
    "rolled-back": "rolled back",
    cancelled: "cancelled",
    skipped: "skipped",
    running: "deploying",
    queued: "queued"
};

export const DEPLOY_TONE = {
    success: "up",
    failed: "down",
    "rolled-back": "failing",
    cancelled: "unknown",
    skipped: "unknown",
    running: "running",
    queued: "unknown"
};

/**
 * Deployments, incidents and certificates as one stream, newest first.
 *
 * Built here rather than sent, because each of those is already on the board
 * in its own shape and the stream is only another way of reading them. The
 * notifications are not merged in: they say the same things again, for a
 * phone, and a wall that repeats itself is noise.
 */
export function feedOf(board) {
    if (!board) return [];
    const events = [];

    for (const deployment of board.deployments.active) {
        events.push({
            id: `deploy:${deployment.id}`,
            at: deployment.startedAt ?? deployment.queuedAt,
            kind: "deploy",
            tone: DEPLOY_TONE[deployment.status] ?? "unknown",
            live: deployment.status === "running",
            title: `${deployment.slug} #${deployment.number}`,
            detail: `${DEPLOY_WORD[deployment.status]} · ${deployment.commit.shortSha ?? ""} ${deployment.commit.message ?? ""}`.trim()
        });
    }
    for (const deployment of board.deployments.recent) {
        events.push({
            id: `deploy:${deployment.id}`,
            at: deployment.endedAt ?? deployment.startedAt,
            kind: "deploy",
            tone: DEPLOY_TONE[deployment.status] ?? "unknown",
            title: `${deployment.slug} #${deployment.number}`,
            detail: `${DEPLOY_WORD[deployment.status] ?? deployment.status} in ${took(deployment.durationMs)} · ${deployment.commit.message ?? deployment.reason ?? ""}`.trim()
        });
    }
    for (const incident of board.incidents.open) {
        events.push({
            id: `incident:${incident.id}`,
            at: incident.startedAt,
            kind: "incident",
            tone: "down",
            live: true,
            title: incident.name,
            detail: `not answering · ${incident.reason || incident.group}`
        });
    }
    for (const incident of board.incidents.recent) {
        events.push({
            id: `incident:${incident.id}:closed`,
            at: incident.endedAt,
            kind: "recovered",
            tone: "up",
            title: incident.name,
            detail: `answering again · was down for ${took(incident.durationMs)}`
        });
    }
    for (const cert of board.certificates.expiring) {
        events.push({
            id: `cert:${cert.domain}`,
            at: cert.expiresAt,
            kind: "certificate",
            tone: cert.daysLeft < 0 ? "down" : "failing",
            title: cert.domain,
            detail: cert.daysLeft < 0 ? `certificate expired ${-cert.daysLeft}d ago` : `certificate expires in ${cert.daysLeft}d`
        });
    }

    return events.sort((a, b) => new Date(b.at ?? 0) - new Date(a.at ?? 0));
}
